import { Component, OnInit, ElementRef, ViewChild, HostListener } from '@angular/core';
import { JWTTokenServiceService } from 'src/app/public/services/token.service';
import { HomeService } from '../../services/home.service';
import { AnalyticsService } from '../../services/analytics.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { PermissionService } from '../../services/permission.service';
import { UsersService } from '../../services/users.service';
import { LeadService } from '../../services/lead.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  isMobileView = window.innerWidth <= 1024;
  active = 1;
  data: any;
  leadLoading: boolean = false;
  appointmentLoading: boolean = false;
  leadsAnalytics: any;
  appointmentAnalytics: any;
  opthoAnalytics: any;
  opthoAnalyticsLoading: boolean = false;
  currentTab = "leadAnalytics"; // Initialize as leadAnalytics
  totalLeads: any = 0;
  chatBotAnalytics: any;
  chatBotLoading: boolean = false;

  // Date filter state
  leadActiveFilter: string = 'this_year';
  appointmentActiveFilter: string = 'this_year';

  // Custom date range
  showLeadCustomRange: boolean = false;
  showAppointmentCustomRange: boolean = false;
  leadCustomStart: string = '';
  leadCustomEnd: string = '';
  appointmentCustomStart: string = '';
  appointmentCustomEnd: string = '';
  todayStr: string = new Date().toISOString().split('T')[0];

  dateFilters = [
    { key: 'this_week',   label: 'This Week' },
    { key: 'last_week',   label: 'Last Week' },
    { key: 'this_month',  label: 'This Month' },
    { key: 'last_month',  label: 'Last Month' },
    { key: 'this_year',   label: 'This Year' },
    { key: 'last_year',   label: 'Last Year' },
    { key: 'custom',      label: 'Custom Range' },
  ];

  // Lead Analytics & Lead Communication Dedicated State
  users: any = [];
  selectedUser: any = "";
  selectedYear: any = new Date().getFullYear().toString();
  years: any[] = ["2020", "2021", "2022", "2023", "2024", "2025"];
  isUserLoading: boolean = false;
  isAdmin: boolean = false;

  dedicatedLeadsAnalytics: any = {};
  dedicatedTotalLeads: number = 0;
  dedicatedPipelineStages: any[] = [];
  dedicatedSelectedPipelineId: number | null = null;
  dedicatedIsPipelineLoading: boolean = false;
  dedicatedDefaultPipelineStages: any[] = [];
  dedicatedLeadsCards: any[] = [];
  dedicatedLeadLoading: boolean = false;

  constructor(
    private jwt: JWTTokenServiceService,
    private api: AnalyticsService,
    private localStorage: LocalStorageService,
    private checkPermission: PermissionService,
    private userApi: UsersService,
    private leadApi: LeadService
  ) {}

  /** Compute start_date / end_date for a given filter key */
  private getDateRange(filterKey: string, customStart?: string, customEnd?: string): { start_date: string; end_date: string } {
    const now = new Date();
    const fmt = (d: Date) => d.toISOString().split('T')[0];

    switch (filterKey) {
      case 'this_week': {
        const day = now.getDay();
        const mon = new Date(now); mon.setDate(now.getDate() - ((day + 6) % 7));
        const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
        return { start_date: fmt(mon), end_date: fmt(sun) };
      }
      case 'last_week': {
        const day = now.getDay();
        const thisMon = new Date(now); thisMon.setDate(now.getDate() - ((day + 6) % 7));
        const lastMon = new Date(thisMon); lastMon.setDate(thisMon.getDate() - 7);
        const lastSun = new Date(thisMon); lastSun.setDate(thisMon.getDate() - 1);
        return { start_date: fmt(lastMon), end_date: fmt(lastSun) };
      }
      case 'this_month': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return { start_date: fmt(start), end_date: fmt(end) };
      }
      case 'last_month': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end   = new Date(now.getFullYear(), now.getMonth(), 0);
        return { start_date: fmt(start), end_date: fmt(end) };
      }
      case 'this_year': {
        return { start_date: `${now.getFullYear()}-01-01`, end_date: `${now.getFullYear()}-12-31` };
      }
      case 'last_year': {
        const y = now.getFullYear() - 1;
        return { start_date: `${y}-01-01`, end_date: `${y}-12-31` };
      }
      case 'custom': {
        return { start_date: customStart || fmt(now), end_date: customEnd || fmt(now) };
      }
      default:
        return { start_date: `${now.getFullYear()}-01-01`, end_date: `${now.getFullYear()}-12-31` };
    }
  }

  _check(module: any, feature: any) {

    if (this.checkPermission.getAccess(module) && this.checkPermission.getfeatureAccess(module, feature)) {
      return true
    } else {
      return false
    }

  }
  ngOnInit(): void {
    this.fnGetLeadAnalytics();

    // Lead Analytics & Lead Communication Dedicated Setup
    const role = this.localStorage.getRole();
    this.isAdmin = ['Administrator', 'Admin', 'admin', 'administrator'].includes(role);

    this.loadDedicatedPipelineStages();
    this.fnGetDedicatedLeadAnalytics();
    this.getAllUsers();

    const currentYear = new Date().getFullYear();
    if (!this.years.includes(currentYear.toString())) {
      this.years.push(currentYear.toString());
    }
  }

  getAllUsers() {
    const param = {
      where: {
        hospital_id: this.localStorage.getHospitalId(),
        center_id: this.localStorage.getCenterId()
      },
      page: 0,
      limit: 1000,
      filter: null
    };
    this.isUserLoading = true;
    this.userApi.findAllUsers(param).subscribe({
      next: (res: any) => {
        const usersData = res?.rows || res || [];
        this.users = usersData.map((user: any) => {
          return {
            ...user,
            name: [user.fname, user.lname].join(' '),
            id: user.user_id
          };
        });

        if (this.isAdmin) {
          this.users.unshift({ name: 'All Users', id: '' });
        }
        this.isUserLoading = false;
      },
      error: (e: any) => {
        console.error(e);
        this.isUserLoading = false;
      },
    });
  }

  loadDedicatedPipelineStages() {
    let param = {
      hospital_id: this.localStorage.getHospitalId(),
      pipeline_type: 'lead'
    };
    this.dedicatedIsPipelineLoading = true;
    this.leadApi.getPipelines(param).subscribe({
      next: (res: any) => {
        if (res && res.length > 0) {
          const defaultPipeline = res.find((pipeline: any) => pipeline.set_default === true);
          if (defaultPipeline) {
            this.dedicatedSelectedPipelineId = defaultPipeline.id;
            this.dedicatedDefaultPipelineStages = defaultPipeline.stages;
            if (this.dedicatedLeadsAnalytics && this.dedicatedLeadsAnalytics.leads_status) {
              this.updateDedicatedLeadStatusFromPipeline(defaultPipeline.stages);
            }
          }
          this.dedicatedPipelineStages = res.map((pipeline: any) => ({
            label: pipeline.pipeline_name,
            value: pipeline.id,
            stages: pipeline.stages
          }));
        }
        this.dedicatedIsPipelineLoading = false;
      },
      error: (e: any) => {
        console.error('Error loading pipeline stages:', e);
        this.dedicatedIsPipelineLoading = false;
      },
    });
  }

  onDedicatedPipelineChange(event: any) {
    const selectedPipeline = this.dedicatedPipelineStages.find(p => p.value === Number(event.target.value));
    if (selectedPipeline && selectedPipeline.stages) {
      this.updateDedicatedLeadStatusFromPipeline(selectedPipeline.stages);
    }
  }

  updateDedicatedLeadStatusFromPipeline(stages: any[]) {
    const newLeadsStatus: any = {};
    stages.forEach((stage: any) => {
      newLeadsStatus[stage.stage_name] = this.dedicatedLeadsAnalytics?.leads_status?.[stage.stage_name] || "0";
    });
    this.dedicatedLeadsAnalytics.leads_status = newLeadsStatus;
  }

  fnChangeUser(event: any) {
    this.selectedUser = event;
    this.fnGetDedicatedLeadAnalytics();
  }

  fnChangeYear(event: any) {
    this.selectedYear = event;
    this.fnGetDedicatedLeadAnalytics();
  }

  fnGetDedicatedLeadAnalytics() {
    const start_date = `${this.selectedYear}-01-01`;
    const end_date = `${this.selectedYear}-12-31`;

    let param: any = {
      hospital_id: this.localStorage.getHospitalId(),
      start_date,
      end_date
    };

    if (this.selectedUser && this.selectedUser !== "") {
      param.user_id = this.selectedUser;
    }

    this.dedicatedLeadLoading = false;
    this.api.getLeadAnalytics(param).subscribe({
      next: (res) => {
        if (res) {
          this.dedicatedLeadsAnalytics = res;
          this.dedicatedLeadLoading = true;
          this.dedicatedTotalLeads = res.total_leads;
          this.dedicatedLeadsCards = [
            {
              title: 'Total Leads',
              icon: 'fas fa-users',
              value: this.dedicatedLeadsAnalytics?.total_leads || 0
            },
            {
              title: 'Converted Leads',
              icon: 'fas fa-check-circle',
              value: this.dedicatedLeadsAnalytics?.leads_status?.['Converted'] || 0
            },
            {
              title: 'Hot Leads',
              icon: 'fas fa-fire',
              value: this.dedicatedLeadsAnalytics?.leads_types?.['Hot Leads'] || 0
            },
            {
              title: 'Cold Leads',
              icon: 'fas fa-snowflake',
              value: this.dedicatedLeadsAnalytics?.leads_types?.['Cold Leads'] || 0
            }
          ];

          if (this.dedicatedDefaultPipelineStages.length > 0) {
            this.updateDedicatedLeadStatusFromPipeline(this.dedicatedDefaultPipelineStages);
          }
        }
      },
      error: (e) => {
        this.dedicatedLeadLoading = true;
        this.dedicatedLeadsAnalytics = {
          "platform_wise_leads": {
            "tina": { "Jan": 0, "Feb": 0, "Mar": 0, "Apr": 0, "May": 0, "Jun": 0, "Jul": 0, "Aug": 0, "Sep": 0, "Oct": 0, "Nov": 0, "Dec": 0 },
            "manual": { "Jan": 0, "Feb": 0, "Mar": 0, "Apr": 0, "May": 0, "Jun": 0, "Jul": 0, "Aug": 0, "Sep": 0, "Oct": 0, "Nov": 0, "Dec": 0 },
            "whatsapp": { "Jan": 0, "Feb": 0, "Mar": 0, "Apr": 0, "May": 0, "Jun": 0, "Jul": 0, "Aug": 0, "Sep": 0, "Oct": 0, "Nov": 0, "Dec": 0 }
          },
          "leads_types": {
            "Hot Leads": "0",
            "Cold Leads": "0",
            "Warm Leads": "0",
            "New Leads": "0"
          },
          "leads_status": {
            "Completed": "0",
            "Follow Up": "0",
            "Communicated": "0",
            "Pending": "0"
          },
          "total_leads": "0",
          "completed_leads": "0",
          "pending_leads": "0",
          "communicated_leads": "0",
          "followup_leads": "0"
        };
      }
    });
  }

  fnChangeTab(event: any) {
    this.currentTab = event;
    console.log(event)
    if (event == 'appointment') {
      this.fnGetAppointmentAnalytics();
    }
    else if (event == 'lead' || event == 'communication') {
      this.fnGetLeadAnalytics();
    }
    else if (event == 'leadAnalytics' || event == 'leadCommunication') {
      this.fnGetDedicatedLeadAnalytics();
    }
    else if (event == 'chatbot') {
      this.fnChatbotAnalyticsData();
    }
    else if (event == 'Optometrist') {
      console.log("hello")
      this.opthoAnalytics = {
        platform_wise_appointments: { "tina": { "Apr": 0, "Mar": 10, "Feb": 2, "Jan": 0, "Dec": 0, "Nov": 0, "Oct": 0, "Sep": 0, "Aug": 0, "Jul": 0, "Jun": 0, "May": 0 }, "manual": { "Apr": 0, "Mar": 0, "Feb": 14, "Jan": 1, "Dec": 0, "Nov": 0, "Oct": 0, "Sep": 0, "Aug": 0, "Jul": 0, "Jun": 0, "May": 0 }, "whatsapp": { "Apr": 0, "Mar": 0, "Feb": 0, "Jan": 0, "Dec": 0, "Nov": 0, "Oct": 0, "Sep": 0, "Aug": 0, "Jul": 0, "Jun": 0, "May": 0 } }
      }
      this.opthoAnalyticsLoading = true;
    }

    console.log("opto", this.opthoAnalytics)
    console.log("leads", this.opthoAnalyticsLoading)
  }

  fnChatbotAnalyticsData() {
    this.chatBotLoading = false;
    let param = {
      // client_id: this.localStorage.getClientId(),
      client_id: "6f20b077-dc46-467d-9e80-fd7027169e9d",
    }

    this.api.getChatbotAnalytics(param).subscribe({
      next: (res) => {
        console.log(res)
        if (res) {
          this.chatBotLoading = true;
          this.chatBotAnalytics = res;
          this.chatBotAnalytics.user_ratings = {
            "5": 80,
            "4": 45,
            "3": 15,
            "2": 10,
            "1": 3,
          }
          this.chatBotAnalytics.user_feedbacks = {
            "negative": 0,
            "positive": 52
          }
        } else {
          this.chatBotLoading = true;
          this.chatBotAnalytics = {
            "user_data": {
              "0": 0,
              "1": 0,
              "2": 0,
              "3": 0,
              "4": 0,
              "5": 0,
              "6": 0,
              "7": 0,
              "8": 0,
              "9": 0,
              "10": 0,
              "11": 0
            },
            "common_disease": "",
            "avg_waiting_time": 0,
            "hot_leads": 0,
            "total_users": 0,
            "total_messages": 0,
            "old_appointment": 0,
            "active_hours": 0,
            "top_active_hours": {
              "13:00-14:00": 0,
              "15:00-16:00": 0,
              "11:00-12:00": 0,
              "09:00-10:00": 0,
              "05:00-06:00": 0
            },
            "unique_users": "0",
            "total_leads_last_month": {
              "whatsapp": 0,
              "youtube": 0,
              "total": 0,
              "twitter": 0,
              "messenger": 0,
              "facebook": 0,
              "instagram": 0,
              "linkedin": 0,
              "tina": 0
            },
            "new_appointment": 0,
            "user_feedbacks": {
              "negative": 0,
              "positive": 0
            },
            "user_ratings": {
              "5": 0,
              "4": 0,
              "3": 0,
              "2": 0,
              "1": 0,
            },
            "total_ratings": 0,
            "cold_leads": 0,
            "leads": {
              "whatsapp": {
                "0": 0,
                "1": 0,
                "2": 0,
                "3": 0,
                "4": 0,
                "5": 0,
                "6": 0,
                "7": 0,
                "8": 0,
                "9": 0,
                "10": 0,
                "11": 0
              },
              "youtube": {
                "0": 0,
                "1": 0,
                "2": 0,
                "3": 0,
                "4": 0,
                "5": 0,
                "6": 0,
                "7": 0,
                "8": 0,
                "9": 0,
                "10": 0,
                "11": 0
              },
              "twitter": {
                "0": 0,
                "1": 0,
                "2": 0,
                "3": 0,
                "4": 0,
                "5": 0,
                "6": 0,
                "7": 0,
                "8": 0,
                "9": 0,
                "10": 0,
                "11": 0
              },
              "messenger": {
                "0": 0,
                "1": 0,
                "2": 0,
                "3": 0,
                "4": 0,
                "5": 0,
                "6": 0,
                "7": 0,
                "8": 0,
                "9": 0,
                "10": 0,
                "11": 0
              },
              "facebook": {
                "0": 0,
                "1": 0,
                "2": 0,
                "3": 0,
                "4": 0,
                "5": 0,
                "6": 0,
                "7": 0,
                "8": 0,
                "9": 0,
                "10": 0,
                "11": 0
              },
              "instagram": {
                "0": 0,
                "1": 0,
                "2": 0,
                "3": 0,
                "4": 0,
                "5": 0,
                "6": 0,
                "7": 0,
                "8": 0,
                "9": 0,
                "10": 0,
                "11": 0
              },
              "linkedin": {
                "0": 0,
                "1": 0,
                "2": 0,
                "3": 0,
                "4": 0,
                "5": 0,
                "6": 0,
                "7": 0,
                "8": 0,
                "9": 0,
                "10": 0,
                "11": 0
              },
              "tina": {
                "0": 0,
                "1": 0,
                "2": 0,
                "3": 0,
                "4": 0,
                "5": 0,
                "6": 0,
                "7": 0,
                "8": 0,
                "9": 0,
                "10": 0,
                "11": 0
              }
            },
            "total_app": 0,
            "populer_topic": {},
            "pending_appointment": 0,
            "location": "Asia/Calcutta",
            "done_appointment": 0,
            "client_id": "b0808768-9544-4e5d-820c-9f1f35402e9c",
            "total_leads": 0,
            "popular_topic": {
              "Glaucoma": "0",
              "Cataract": "0",
              "clinic": "0",
              "cataract": "0"
            },
            "total_feedback": 0,
            "appStatus": {
              "confirmed": 0,
              "cancelled": 0,
              "rescheduled": 0
            },
            "top_intent": {
              "Appointment_book_bot": "0",
              "Appointment_book_bot_demo": "0",
              "User_Form": "0",
              "TinaOphthalWelcome": "0",
              "Ophthal_Treatment": "0"
            },
            "total_appointments": 0
          }
        }


      },
      error: (e) => {
        this.chatBotLoading = true;
        this.chatBotAnalytics = {
          "user_data": {
            "0": 0,
            "1": 0,
            "2": 0,
            "3": 0,
            "4": 0,
            "5": 0,
            "6": 0,
            "7": 0,
            "8": 0,
            "9": 0,
            "10": 0,
            "11": 0
          },
          "common_disease": "",
          "avg_waiting_time": 0,
          "hot_leads": 0,
          "total_users": 0,
          "total_messages": 0,
          "old_appointment": 0,
          "active_hours": 0,
          "top_active_hours": {
            "13:00-14:00": 0,
            "15:00-16:00": 0,
            "11:00-12:00": 0,
            "09:00-10:00": 0,
            "05:00-06:00": 0
          },
          "unique_users": "0",
          "total_leads_last_month": {
            "whatsapp": 0,
            "youtube": 0,
            "total": 0,
            "twitter": 0,
            "messenger": 0,
            "facebook": 0,
            "instagram": 0,
            "linkedin": 0,
            "tina": 0
          },
          "new_appointment": 0,
          "user_feedbacks": {
            "negative": 0,
            "positive": 0
          },
          "user_ratings": {
            "5": 0,
            "4": 0,
            "3": 0,
            "2": 0,
            "1": 0,
          },
          "total_ratings": 0,
          "cold_leads": 0,
          "leads": {
            "whatsapp": {
              "0": 0,
              "1": 0,
              "2": 0,
              "3": 0,
              "4": 0,
              "5": 0,
              "6": 0,
              "7": 0,
              "8": 0,
              "9": 0,
              "10": 0,
              "11": 0
            },
            "youtube": {
              "0": 0,
              "1": 0,
              "2": 0,
              "3": 0,
              "4": 0,
              "5": 0,
              "6": 0,
              "7": 0,
              "8": 0,
              "9": 0,
              "10": 0,
              "11": 0
            },
            "twitter": {
              "0": 0,
              "1": 0,
              "2": 0,
              "3": 0,
              "4": 0,
              "5": 0,
              "6": 0,
              "7": 0,
              "8": 0,
              "9": 0,
              "10": 0,
              "11": 0
            },
            "messenger": {
              "0": 0,
              "1": 0,
              "2": 0,
              "3": 0,
              "4": 0,
              "5": 0,
              "6": 0,
              "7": 0,
              "8": 0,
              "9": 0,
              "10": 0,
              "11": 0
            },
            "facebook": {
              "0": 0,
              "1": 0,
              "2": 0,
              "3": 0,
              "4": 0,
              "5": 0,
              "6": 0,
              "7": 0,
              "8": 0,
              "9": 0,
              "10": 0,
              "11": 0
            },
            "instagram": {
              "0": 0,
              "1": 0,
              "2": 0,
              "3": 0,
              "4": 0,
              "5": 0,
              "6": 0,
              "7": 0,
              "8": 0,
              "9": 0,
              "10": 0,
              "11": 0
            },
            "linkedin": {
              "0": 0,
              "1": 0,
              "2": 0,
              "3": 0,
              "4": 0,
              "5": 0,
              "6": 0,
              "7": 0,
              "8": 0,
              "9": 0,
              "10": 0,
              "11": 0
            },
            "tina": {
              "0": 0,
              "1": 0,
              "2": 0,
              "3": 0,
              "4": 0,
              "5": 0,
              "6": 0,
              "7": 0,
              "8": 0,
              "9": 0,
              "10": 0,
              "11": 0
            }
          },
          "total_app": 0,
          "populer_topic": {},
          "pending_appointment": 0,
          "location": "Asia/Calcutta",
          "done_appointment": 0,
          "client_id": "b0808768-9544-4e5d-820c-9f1f35402e9c",
          "total_leads": 0,
          "popular_topic": {
            "Glaucoma": "0",
            "Cataract": "0",
            "clinic": "0",
            "cataract": "0"
          },
          "total_feedback": 0,
          "appStatus": {
            "confirmed": 0,
            "cancelled": 0,
            "rescheduled": 0
          },
          "top_intent": {
            "Appointment_book_bot": "0",
            "Appointment_book_bot_demo": "0",
            "User_Form": "0",
            "TinaOphthalWelcome": "0",
            "Ophthal_Treatment": "0"
          },
          "total_appointments": 0
        }
      },
      complete: () => { }
    })




  }

  onLeadFilterChange(filterKey: string) {
    this.leadActiveFilter = filterKey;
    this.showLeadCustomRange = filterKey === 'custom';
    if (filterKey !== 'custom') {
      this.leadLoading = false;
      this.fnGetLeadAnalytics();
    }
  }

  applyLeadCustomRange() {
    if (!this.leadCustomStart || !this.leadCustomEnd) return;
    this.leadLoading = false;
    this.fnGetLeadAnalytics();
  }

  fnGetLeadAnalytics() {
    const { start_date, end_date } = this.getDateRange(this.leadActiveFilter, this.leadCustomStart, this.leadCustomEnd);
    const param = {
      hospital_id: this.localStorage.getHospitalId(),
      start_date,
      end_date
    };
    this.api.getLeadAnalytics(param).subscribe({
      next: (res) => {
        if (res) {
          this.leadsAnalytics = res;
          this.leadLoading = true;
          this.totalLeads = res.total_leads;
        }
      },
      error: (e) => {
        this.leadLoading = true;
        this.leadsAnalytics = {
          "platform_wise_leads": {
            "tina":    { "Mar": 0, "Feb": 0, "Jan": 0, "Dec": 0, "Nov": 0, "Oct": 0, "Sep": 0, "Aug": 0, "Jul": 0, "Jun": 0, "May": 0, "Apr": 0 },
            "manual":  { "Mar": 0, "Feb": 0, "Jan": 0, "Dec": 0, "Nov": 0, "Oct": 0, "Sep": 0, "Aug": 0, "Jul": 0, "Jun": 0, "May": 0, "Apr": 0 },
            "whatsapp":{ "Mar": 0, "Feb": 0, "Jan": 0, "Dec": 0, "Nov": 0, "Oct": 0, "Sep": 0, "Aug": 0, "Jul": 0, "Jun": 0, "May": 0, "Apr": 0 }
          },
          "leads_types":  { "Hot Leads": "0", "Cold Leads": "0", "Warm Leads": "0", "New Leads": "0" },
          "leads_status": { "Completed": "0", "Follow Up": "0", "Communicated": "0", "Pending": "0" },
          "total_leads": "0", "completed_leads": "0", "pending_leads": "0",
          "communicated_leads": "0", "followup_leads": "0"
        };
      }
    });
  }
  onAppointmentFilterChange(filterKey: string) {
    this.appointmentActiveFilter = filterKey;
    this.showAppointmentCustomRange = filterKey === 'custom';
    if (filterKey !== 'custom') {
      this.appointmentLoading = false;
      this.fnGetAppointmentAnalytics();
    }
  }

  applyAppointmentCustomRange() {
    if (!this.appointmentCustomStart || !this.appointmentCustomEnd) return;
    this.appointmentLoading = false;
    this.fnGetAppointmentAnalytics();
  }

  fnGetAppointmentAnalytics() {
    const { start_date, end_date } = this.getDateRange(this.appointmentActiveFilter, this.appointmentCustomStart, this.appointmentCustomEnd);
    const param = {
      hospital_id: this.localStorage.getHospitalId(),
      start_date,
      end_date
    };
    this.api.getAppointmentAnalytics(param).subscribe({
      next: (res) => {
        if (res) {
          this.appointmentAnalytics = res;
          this.appointmentLoading = true;
        }
      },
      error: (e) => {
        this.appointmentLoading = true;
        this.appointmentAnalytics = {
          "platform_wise_appointments": {
            "tina":    { "Mar": 0, "Feb": 0, "Jan": 0, "Dec": 0, "Nov": 0, "Oct": 0, "Sep": 0, "Aug": 0, "Jul": 0, "Jun": 0, "May": 0, "Apr": 0 },
            "manual":  { "Mar": 0, "Feb": 0, "Jan": 0, "Dec": 0, "Nov": 0, "Oct": 0, "Sep": 0, "Aug": 0, "Jul": 0, "Jun": 0, "May": 0, "Apr": 0 },
            "whatsapp":{ "Mar": 0, "Feb": 0, "Jan": 0, "Dec": 0, "Nov": 0, "Oct": 0, "Sep": 0, "Aug": 0, "Jul": 0, "Jun": 0, "May": 0, "Apr": 0 }
          },
          "appointment_types":  { "New": "0", "Follow Up": "0" },
          "appointment_visits": { "Clinic": "0", "Online": "0", "Home Visit": "0" },
          "appointment_status": { "Completed": "0", "Check In": "0", "Check Out": "0", "Pending": 0, "Rescheduled": "0", "Cancelled": "0" },
          "total": "0", "completed": "0", "pending": 0, "check_in": "0", "cancelled": "0"
        };
      }
    });
  }
  @HostListener('window:resize', ['$event'])
    onResize(event: Event) {
      this.isMobileView = window.innerWidth <= 1024;
    }
  
  handleScrollLeft()  {
    const container = this.scrollContainer.nativeElement;
    container.scrollBy({ left: -150, behavior: 'smooth' });
  }
  
  handleScrollRight() {
    const container = this.scrollContainer.nativeElement;
    container.scrollBy({ left: 150, behavior: 'smooth' });
  }

}
